import { auth, guest, requestAccessMiddleware } from "@/middleware/auth";
import { createRouter, createWebHistory } from "vue-router";
import { beginChunkRecovery, isChunkAssetUrl, isChunkLoadError, isChunkParseError } from "@/utils/staleChunk";

import {
    activeMemberGuard,
    completedProfileGuard,
    onboardingCompleteGuard,
    onboardingIncompleteGuard,
    organizationSubdomainGuard,
    partiallyCompletedProfileGuard,
    requestAccess,
    themeSettingsGuard,
    adminOrRepresentativeGuard,
    organizationOnlineGuard,
    superAdminGuard,
    benefitsAdminGuard,
    appHostOrOnlineOrganizationGuard
} from "@/guards";
import {checkFeatureGuard} from "@/guards/checkFeatureGuard";
import {reconciliationAccessGuard} from "@/guards/reconciliationAccessGuard";
import {lifeBenefitsGuard} from "@/guards/lifeBenefitsGuard";
const ProfileBuilder = () => import(/* webpackPrefetch: true */ "@/views/admin/members/ProfileBuilder.vue");
const Import = () => import(/* webpackPrefetch: true */'@/views/admin/members/Import.vue');
const ImportMembers = () => import(/* webpackPrefetch: true */'@/views/ImportMembers.vue');
const InviteUser = () => import(/* webpackPrefetch: true */'@/views/admin/members/InviteUser.vue');
const ReviewMember = () => import(/* webpackPrefetch: true */'@/views/admin/members/ReviewMember_New.vue');
const Groups = () => import(/* webpackPrefetch: true */'@/views/dashboard/Groups');
const CreateGroup = () => import(/* webpackPrefetch: true */'@/views/admin/groups/Create.vue');
const EditGroup = () => import(/* webpackPrefetch: true */'@/views/admin/groups/Edit.vue');
const GroupDetail = () => import(/* webpackPrefetch: true */'@/views/dashboard/groups/GroupDetail.vue');
const FieldAutomations = () => import(/* webpackPrefetch: true */'@/views/dashboard/FieldAutomations.vue');
const CreateSms = () => import(/* webpackPrefetch: true */'@/views/admin/sms/Create.vue');
const CreateEmail = () => import(/* webpackPrefetch: true */'@/views/admin/email/Create.vue');
const Inbox = () => import(/* webpackPrefetch: true */'@/views/admin/inbox/Inbox.vue');
const Login = () => import(/* webpackPrefetch: true */'@/views/login/Login.vue');
const PasswordLogin = () => import('@/views/login/PasswordLogin.vue');
const RequestAccess = () => import(/* webpackPrefetch: true */'@/views/RequestAccess.vue');
const Register = () => import(/* webpackPrefetch: true */'@/views/register/Register.vue');
const Invitation = () => import(/* webpackPrefetch: true */'@/views/invitation/Invitation.vue');
const MemberProfile = () => import(/* webpackPrefetch: true */'@/views/dashboard/profile/MemberProfile.vue');
const Onboarding = () => import(/* webpackPrefetch: true */'@/views/dashboard/Onboarding.vue');
const OnboardingReminder = () => import(/* webpackPrefetch: true */'@/views/guards/OnboardingReminder.vue');
const CompleteProfileReminder = () => import(/* webpackPrefetch: true */'@/views/guards/CompleteProfileReminder.vue');
const Notifications = () => import(/* webpackPrefetch: true */'@/views/dashboard/Notifications');
const Chat = () => import(/* webpackPrefetch: true */'@/views/chat/Chat.vue');
const Launchpad = () => import(/* webpackPrefetch: true */'@/views/dashboard/Launchpad.vue');
const Dashboard = () => import(/* webpackPrefetch: true */'@/views/dashboard/Dashboard.vue');
const CompleteOrganizationInfo = () => import(/* webpackPrefetch: true */'@/views/dashboard/profile/CompleteOrganizationInfo.vue');
const MembersList = () => import(/* webpackPrefetch: true */'@/views/dashboard/members/MembersList.vue');
const AccessDenied = () => import(/* webpackPrefetch: true */'@/views/guards/AccessDenied.vue');
const FeatureUnavailable = () => import(/* webpackPrefetch: true */'@/views/guards/FeatureUnavailable.vue');
const ThemeSettings = () => import(/* webpackPrefetch: true */'@/views/dashboard/ThemeSettings.vue');
const NotFound = () => import(/* webpackPrefetch: true */'@/views/NotFound.vue');
const CreateNews = () => import(/* webpackPrefetch: true */'@/views/news/CreateNews.vue');
const News = () => import(/* webpackPrefetch: true */'@/views/news/NewsList.vue');
const NewsArticle = () => import(/* webpackPrefetch: true */'@/views/news/NewsArticle.vue');
const PublicNews = () => import(/* webpackPrefetch: true */'@/views/news/Public/PublicNews.vue');
const Resources = () => import(/* webpackPrefetch: true */'@/views/admin/resources/Resources.vue');
const CreateExternalLink = () => import(/* webpackPrefetch: true */'@/views/resources/CreateWeblink.vue');
const CreatePage = () => import(/* webpackPrefetch: true */'@/views/resources/CreatePage.vue');
const ResourcesList = () => import(/* webpackPrefetch: true */'@/views/admin/resources/List.vue');
const PublicArticle = () => import(/* webpackPrefetch: true */'@/views/news/Public/PublicArticle.vue');
const UnsubscribeEmails = () => import('@/views/email-subscription/Unsubscribe.vue');
const PageResource = () => import('@/views/resources/PageResource.vue');
const ReorderDashboard = () => import('@/views/dashboard/ReorderDashboard.vue');
const DocumentsList = () => import(/* webpackPrefetch: true */'@/views/documents/List.vue');
const PaymentsHistory = () => import(/* webpackPrefetch: true */'@/views/admin/payments/PaymentsHistory.vue');
const PaymentView = () => import(/* webpackPrefetch: true */'@/views/admin/payments/PaymentView.vue');
const PaymentDefinitions = () => import(/* webpackPrefetch: true */'@/views/admin/payments/PaymentDefinitions.vue');
const BatchStatus = () => import(/* webpackPrefetch: true */'@/views/admin/batch/BatchStatus.vue');

const BenefitDefinitions = () => import(/* webpackPrefetch: true */'@/views/admin/BenefitDefinitions/BenefitDefinitions.vue');
const BenefitsDashboard = () => import(/* webpackPrefetch: true */'@/views/admin/BenefitsDashboard/BenefitsDashboard.vue');
const EnrollmentsDashboard = () => import(/* webpackPrefetch: true */'@/views/admin/EnrollmentsDashboard/EnrollmentsDashboard.vue');
const PoliciesList = () => import(/* webpackPrefetch: true */'@/views/admin/Policies/PoliciesList.vue');
const HowToGuide = () => import(/* webpackPrefetch: true */'@/views/HowToGuide.vue');
const Calendar = () => import(/* webpackPrefetch: true */'@/views/calendar/CalendarView.vue');
const EventCreate = () => import(/* webpackPrefetch: true */'@/views/calendar/EventCreate.vue');
const EventDetail = () => import(/* webpackPrefetch: true */'@/views/calendar/EventDetail.vue');

const ReconciliationsList = () => import(/* webpackPrefetch: true */'@/views/Reconciliations/ReconciliationsListView.vue');
const ReconciliationUpload = () => import(/* webpackPrefetch: true */'@/views/Reconciliations/ReconciliationUploadView.vue');
const ReconciliationMapping = () => import(/* webpackPrefetch: true */'@/views/Reconciliations/ReconciliationMappingView.vue');
const ReconciliationPrimaryKey = () => import(/* webpackPrefetch: true */'@/views/Reconciliations/ReconciliationPrimaryKeyView.vue');
const ReconciliationRules = () => import(/* webpackPrefetch: true */'@/views/Reconciliations/ReconciliationRulesView.vue');
const ReconciliationResults = () => import(/* webpackPrefetch: true */'@/views/Reconciliations/ReconciliationResultsView.vue');

const routes = [
    {
        path: '/',
        name: 'dashboard',
        component: Dashboard,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/theme-settings',
        name: 'theme-settings',
        component: ThemeSettings,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        },
        props: (route) => ({showModal: route.params.showModal}),
    },
    {
        path: '/launchpad',
        name: 'launchpad',
        component: Launchpad,
        beforeEnter: appHostOrOnlineOrganizationGuard,
        meta: {
            middleware: [auth],
        }
    },
    {
        path: '/onboarding-reminder',
        name: 'onboarding-reminder',
        component: OnboardingReminder,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingIncompleteGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/complete-profile-reminder',
        name: 'complete-profile-reminder',
        component: CompleteProfileReminder,
        beforeEnter: [
            requestAccess,
            partiallyCompletedProfileGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/access-denied',
        name: 'access-denied',
        component: AccessDenied,
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/organization-info',
        name: 'organization-info',
        component: CompleteOrganizationInfo,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            partiallyCompletedProfileGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/view-profile/:uid',
        name: 'view-member-profile',
        component: MemberProfile,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        },
        props: (route) => ({uid: route.params.uid}),
    },
    {
        path: '/profile',
        name: 'profile',
        component: MemberProfile,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        },
    },
    {
        path: '/calendar',
        name: 'calendar',
        component: Calendar,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/calendar/events/create',
        name: 'calendar.events.create',
        component: EventCreate,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/calendar/events/:uid/edit',
        name: 'calendar.events.edit',
        component: EventCreate,
        props: true,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/calendar/events/:uid',
        name: 'calendar.events.show',
        component: EventDetail,
        props: true,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/chat',
        name: 'chat',
        component: Chat,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            checkFeatureGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/onboarding',
        name: 'onboarding',
        component: Onboarding,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingIncompleteGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/notifications',
        name: 'notifications',
        component: Notifications,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/groups',
        name: 'groups',
        component: Groups,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/how-to-guide',
        name: 'how-to-guide',
        component: HowToGuide,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard,
            superAdminGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/field-automations',
        name: 'field-automations',
        component: FieldAutomations,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard,
            superAdminGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/members',
        name: 'members',
        component: MembersList,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            adminOrRepresentativeGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/login',
        name: 'login',
        component: Login,
        beforeEnter: appHostOrOnlineOrganizationGuard,
        meta: {
            middleware: [guest],
            layout: 'DarkLayout'
        }
    },
    {
        path: '/login/password',
        name: 'password-login',
        component: PasswordLogin,
        beforeEnter: appHostOrOnlineOrganizationGuard,
        meta: {
            middleware: [guest],
            layout: 'DarkLayout'
        }
    },
    {
        path: '/request-access',
        name: 'request-access',
        component: RequestAccess,
        beforeEnter: [
            organizationOnlineGuard
        ],
        meta: {
            middleware: [requestAccessMiddleware]
        }
    },
    {
        path: '/feature-unavailable',
        name: 'feature-unavailable',
        component: FeatureUnavailable,
        beforeEnter: [
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/register',
        name: 'register',
        component: Register,
        beforeEnter: appHostOrOnlineOrganizationGuard,
        meta: {
            middleware: [guest],
            layout: 'DarkLayout',
        }
    },
    {
        path: '/invitation/:uid',
        name: 'invitation',
        component: Invitation,
        beforeEnter: [
            organizationOnlineGuard
        ],
        meta: {
            layout: 'DarkLayout'
        }
    },
    {
        path: '/profile-builder',
        name: 'profile-builder',
        component: ProfileBuilder,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/groups/create',
        name: 'create-group',
        component: CreateGroup,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/groups/edit/:uid',
        name: 'edit-group',
        component: EditGroup,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/groups/:uid',
        name: 'group-detail',
        component: GroupDetail,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        },
        props: (route) => ({ uid: route.params.uid || '' })
    },
    {
        path: '/import',
        name: 'import',
        component: Import,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/import-members',
        name: 'import-members',
        component: ImportMembers,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/reconciliations',
        name: 'reconciliations.list',
        component: ReconciliationsList,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            reconciliationAccessGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/reconciliations/new',
        name: 'reconciliations.new',
        component: ReconciliationUpload,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            reconciliationAccessGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/reconciliations/:uid/mapping',
        name: 'reconciliations.mapping',
        component: ReconciliationMapping,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            reconciliationAccessGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/reconciliations/:uid/primary-key',
        name: 'reconciliations.pk',
        component: ReconciliationPrimaryKey,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            reconciliationAccessGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/reconciliations/:uid/rules',
        name: 'reconciliations.rules',
        component: ReconciliationRules,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            reconciliationAccessGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/reconciliations/:uid/results',
        name: 'reconciliations.results',
        component: ReconciliationResults,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            reconciliationAccessGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/review-member/:uid',
        name: 'review-member',
        component: ReviewMember,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        },
        props: (route) => ({uid: route.params.uid || ''}),
    },
    {
        path: '/invite-user',
        name: 'invite-user',
        component: InviteUser,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/sms/create',
        name: 'create-sms',
        component: CreateSms,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/sms/edit/:uid',
        name: 'edit-sms',
        component: CreateSms,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/email/create',
        name: 'create-email',
        component: CreateEmail,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/email/edit/:uid',
        name: 'edit-email',
        component: CreateEmail,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/inbox',
        name: 'inbox',
        component: Inbox,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        },
        props: (route) => ({activeSection: route.params.activeSection || '', activeItem:  route.query.activeItem ? JSON.parse(route.query.activeItem) : null}),
    },
    {
        path:'/news/create',
        name: 'create-news',
        component: CreateNews,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/news/edit/:uid',
        name: 'edit-news',
        component: CreateNews,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/news',
        name: 'news',
        component: News,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/news/:uid',
        name: 'news-article',
        component: NewsArticle,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/public-news',
        name: 'public-news',
        beforeEnter: [
            organizationOnlineGuard
        ],
        component: PublicNews,
    },
    {
        path:'/public-news/:uid',
        name: 'public-article',
        beforeEnter: [
            organizationOnlineGuard
        ],
        component: PublicArticle,
    },
    {
        path: '/unsubscribe/:member',
        name: 'unsubscribe-emails',
        beforeEnter: [
            organizationOnlineGuard
        ],
        component: UnsubscribeEmails,
        meta: {
            layout: 'DarkLayout'
        }
    },
    {
        path: '/resources/create',
        name: 'create-resources',
        component: Resources,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/resources',
        name: 'resources',
        component: ResourcesList,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/resources/view/:uid',
        name: 'view-resource',
        component: PageResource,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: "/resources/create/external-link",
        name: "create-external-link",
        component: CreateExternalLink,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: "NavigationLayout",
        },
    },
    {
        path: '/resources/create/page',
        name: 'create-resource-page',
        component: CreatePage,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout',
        },
    },
    {
        path:'/resources/edit/external-link/:uid',
        name: 'edit-external-link',
        component: CreateExternalLink,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path:'/resources/edit/page/:uid',
        name: 'edit-page',
        component: CreatePage,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/reorder-dashboard',
        name: 'reorder-dashboard',
        component: ReorderDashboard,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/documents/:type(f|d)?/:uid?',
        name: 'documents',
        component: DocumentsList,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/payments',
        name: 'admin.payments.history',
        component: PaymentsHistory,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            adminOrRepresentativeGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/payments/:batchUid',
        name: 'admin.payments.batch',
        component: PaymentView,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            adminOrRepresentativeGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/payment-definitions',
        name: 'admin.payment-definitions',
        component: PaymentDefinitions,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            adminOrRepresentativeGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/batch-status',
        name: 'batch-status',
        component: BatchStatus,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            adminOrRepresentativeGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/benefits-dashboard',
        name: 'benefitsDashboard',
        component: BenefitsDashboard,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            checkFeatureGuard,
            benefitsAdminGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/benefit-definitions',
        name: 'benefitDefinitions',
        component: BenefitDefinitions,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            checkFeatureGuard,
            benefitsAdminGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/life-policies',
        name: 'lifePolicies',
        component: PoliciesList,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            checkFeatureGuard,
            lifeBenefitsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/enrollments-dashboard',
        name: 'enrollmentsDashboard',
        component: EnrollmentsDashboard,
        beforeEnter: [
            requestAccess,
            organizationSubdomainGuard,
            onboardingCompleteGuard,
            completedProfileGuard,
            activeMemberGuard,
            themeSettingsGuard,
            checkFeatureGuard,
            lifeBenefitsGuard,
            organizationOnlineGuard
        ],
        meta: {
            middleware: [auth],
            layout: 'NavigationLayout'
        }
    },
    {
        path: '/:catchAll(.*)',
        component: NotFound,
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes
});

// Reload exactly once so a freshly deployed index.html (and its new chunk
// references) is fetched. The time-windowed guard in staleChunk.js prevents a
// genuinely broken build from reloading in a loop and lets its repeat
// failures reach Sentry; the window expiring re-arms recovery for the next
// stale deploy.
function reloadOnceForStaleChunk() {
    if (beginChunkRecovery()) {
        window.location.reload();
    }
}

router.onError((error, to) => {
    if (!isChunkLoadError(error)) {
        return;
    }

    if (beginChunkRecovery()) {
        window.location.assign(to?.fullPath ?? window.location.href);
    }
});

// router.onError only fires for chunks requested during a route navigation.
// Chunks pulled in at initial pageload (entry runtime, async components
// mounted outside navigation) fail through the global error
// channels instead — surfacing as a resource load error on the <script>/<link>
// element, a parse "Unexpected token '<'" SyntaxError when the server returns
// index.html, or a rejected dynamic import. Recover from those here too.
if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
        const target = event.target;

        if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
            if (target.rel !== 'prefetch' && isChunkAssetUrl(target.src || target.href)) {
                reloadOnceForStaleChunk();
            }
            return;
        }

        // Safari reports parse errors with a null event.error, leaving only
        // the message and the failing script's filename.
        if (isChunkLoadError(event.error)
            || (isChunkParseError(event.message)
                && isChunkAssetUrl(event.filename))) {
            reloadOnceForStaleChunk();
        }
    }, true); // capture: resource load errors do not bubble

    window.addEventListener('unhandledrejection', (event) => {
        if (isChunkLoadError(event.reason)) {
            reloadOnceForStaleChunk();
        }
    });
}

router.beforeEach((to, from, next) => {
    if (!to.meta.middleware) {
        return next()
    }
    const middleware = to.meta.middleware

    const context = {
        to,
        from,
        next,
    }
    return middleware[0]({
        ...context
    })
});

export default router;
